import { LOCALIZATION } from "@prisma/client";

// TH and EN are each other's fallback partner when there's no more specific
// site default to fall back to (e.g. the site's default IS the current locale).
const MAJOR_PARTNER: Partial<Record<LOCALIZATION, LOCALIZATION>> = {
    [LOCALIZATION.TH]: LOCALIZATION.EN,
    [LOCALIZATION.EN]: LOCALIZATION.TH,
};

// Fallback chain per (locale, site default). Always falls back to the site's
// configured default language, including ZH.
export const buildFallbackChain = (
    locale: LOCALIZATION,
    siteDefault: LOCALIZATION,
): LOCALIZATION[] => {
    if (siteDefault === locale) {
        return [MAJOR_PARTNER[locale] ?? LOCALIZATION.EN];
    }

    return [siteDefault];
};

const byLocale = (
    locale: LOCALIZATION,
    th?: string,
    en?: string,
    zh?: string,
): string | undefined => {
    if (locale === LOCALIZATION.TH) return th;
    if (locale === LOCALIZATION.EN) return en;
    return zh;
};

export const pickCartConditionLocale = (
    locale: LOCALIZATION,
    siteDefault: LOCALIZATION,
    th?: string,
    en?: string,
    zh?: string,
): string | undefined => {
    const [fallback] = buildFallbackChain(locale, siteDefault);
    return (
        byLocale(locale, th, en, zh) ||
        byLocale(fallback, th, en, zh) ||
        th ||
        en ||
        zh
    );
};

export const getApiErrorMessage = (
    message: { th?: string; en?: string; zh?: string } | undefined,
    lang: string,
    fallback: string,
): string => {
    const key = lang.toLowerCase();
    return (
        (message as Record<string, string | undefined>)?.[key] ||
        message?.en ||
        message?.th ||
        fallback
    );
};

export const FallbackLocale = <T>(
    attributes: (T & { loc: LOCALIZATION; [key: string]: any })[],
    locale: LOCALIZATION,
    siteDefault: LOCALIZATION,
): T | null => {
    const defaults = buildFallbackChain(locale, siteDefault);
    const excludeKeys = ["meta_title", "meta_description", "meta_keywords", "og"];
    let attribute = attributes.find((f) => f.loc === locale);

    if (!attribute) {
        for (const defLoc of defaults) {
            attribute = attributes.find((f) => f.loc === defLoc);

            if (attribute) {
                break;
            }
        }
    }

    if (!attribute) {
        return null;
    }

    let result = attribute as Record<string, any>;

    for (const key in attribute) {
        if (Object.prototype.hasOwnProperty.call(attribute, key)) {
            if (key === "loc" || excludeKeys.includes(key)) {
                continue;
            }

            let element = attribute[key];

            if (!element) {
                for (const defLoc of defaults) {
                    const fallback = attributes.find((f) => f.loc === defLoc);

                    if (fallback && fallback[key]) {
                        result[key] = fallback[key];
                        break;
                    }
                }
            }
        }
    }

    return (result as T) || null;
};
